import {useTransition} from '@logistics-frontend/blocks'
import {useSupportedCountries} from '@logistics-frontend/client-core/config'
import {useI18n} from '@logistics-frontend/core/i18n'
import {CountryCode} from '@logistics-frontend/core/types'
import {ServerError, useBaseErrorCatcher} from '@logistics-frontend/core/utils/network'
import {isUndefined, map, pick, pipe, uniqBy} from '@logistics-frontend/utils'
import {replaceSearch} from '@logistics-frontend/utils/history'
import {clearSearch} from '@logistics-frontend/utils/window-location'
import {useCallback, useMemo} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

import {TARIFF_LIST_KEY, ZONES_LIST_KEY} from './consts'
import {Category, TariffListRequestArgs, TariffZonesItem} from './types'

export const useTariffZones = (fetchZones: () => Promise<TariffZonesItem[]>) => {
  const errorCatcher = useBaseErrorCatcher()

  const fetchZonesList = useMemo(() => {
    return () => fetchZones().catch(errorCatcher)
  }, [errorCatcher, fetchZones])

  const [{isPending, payload}] = useTransition(fetchZonesList, {key: ZONES_LIST_KEY})

  return {isZonesPending: isPending && isUndefined(payload), tariffZones: payload || []}
}

export const useTariffsList = (
  fetchTariffs: (query: TariffListRequestArgs) => Promise<Category[]>,
  zone: string,
  country: CountryCode,
) => {
  const errorCatcher = useBaseErrorCatcher()
  const [{isPending, payload = []}] = useTransition<Category[] | void, ServerError>(
    zone ? () => fetchTariffs({country, zone}).catch(errorCatcher) : undefined,
    {
      key: TARIFF_LIST_KEY,
    },
  )

  return {isTariffPending: isPending, tariffs: payload}
}

export const useCountriesOptions = (tariffZones: TariffZonesItem[]) => {
  const i18n = useI18n()
  const allCountries = useSupportedCountries()
  const tariffZonesCountries = pipe(
    tariffZones,
    uniqBy(({country}) => country),
    map(({country}) => country),
  )
  const finalCountries = pick(tariffZonesCountries)(allCountries)

  return pipe(
    Object.keys(finalCountries) as CountryCode[],
    map(code => ({
      value: code,
      label: i18n(`country_name.${code}`),
    })),
  )
}

export const useQuerySync = (propName: string, defaultValue: string): [string, (value: string | undefined) => void] => {
  const location = useLocation()
  const history = useHistory()
  const query = new URLSearchParams(clearSearch(location.search))

  const handleChangeProp = useCallback(
    (value: string | undefined) => {
      replaceSearch(history, location, {[propName]: value})
    },
    [history, propName, location],
  )
  return [query.get(propName) ?? defaultValue, handleChangeProp]
}
